import { useSyncExternalStore } from "react"

/**
 * На какой момент показаны данные.
 *
 * ЗАЧЕМ ЭТО ВООБЩЕ ЕСТЬ. Полоса «Нет связи» по макету ВСЕГДА называет время данных:
 * «Показан день на 14:35, изменения сейчас не сохраняются». Без времени полоса
 * бесполезна — человек не знает, устарел экран на минуту или на три часа, и либо
 * перезвонит клиенту зря, либо не перезвонит, когда надо.
 *
 * ПОЧЕМУ ХРАНИЛИЩЕ ВНЕ REACT, А НЕ ПРОПС СВЕРХУ ВНИЗ. Момент знает экран (журнал берёт
 * его из generatedAt ответа), а показывает оболочка — то есть РОДИТЕЛЬ экрана. Тащить
 * значение вверх через контекст значит заставить каждый будущий экран знать про полосу
 * состояния; здесь же экран говорит одну строку, а оболочка слушает.
 *
 * ТОЧКА ПОДКЛЮЧЕНИЯ ДЛЯ ЭКРАНОВ: reportDataAt(result.generatedAt) в эффекте после загрузки.
 */

let dataAt: string | null = null
const listeners = new Set<() => void>()

function publish(next: string | null): void {
  if (next === dataAt) return
  dataAt = next
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): string | null {
  return dataAt
}

/**
 * Экран сообщил момент своих данных.
 *
 * Берётся САМЫЙ ПОЗДНИЙ из известных, а не последний присланный. Иначе переход
 * на закешированный вчерашний день откатил бы подпись назад, и полоса «Нет связи»
 * стала бы утверждать, что всё на экране старше, чем оно есть.
 */
export function reportDataAt(instant: string): void {
  const next = Date.parse(instant)
  if (Number.isNaN(next)) return
  const current = dataAt === null ? Number.NEGATIVE_INFINITY : Date.parse(dataAt)
  if (next <= current) return
  publish(instant)
}

/**
 * Данные только что пришли от сервера, но собственного момента у них нет.
 *
 * Так отмечается загрузка профиля: она и есть первый успешный ответ сервера за сессию.
 * Благодаря ей полоса «Нет связи», выпавшая до открытия журнала, всё равно называет
 * время, а не отделывается словами «данные не загружены».
 */
export function markFreshNow(now: Date = new Date()): void {
  reportDataAt(now.toISOString())
}

/** Забыть момент: смена салона обнуляет всё, что было показано до неё. */
export function resetDataFreshness(): void {
  publish(null)
}

/** Снимок без React. Нужен проверкам и тому коду, что живёт вне дерева компонентов. */
export function getDataAt(): string | null {
  return dataAt
}

export function useDataAt(): string | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
